import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "components/translations/useTranslation";
import Comment from "components/input/Comment.component";
import UserSearch from "components/input/UserSearch.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { FormField } from "components/common/FormField.component";
import { Notification, NotificationType } from "components/common/Notification.component";
import { reassignReview } from "components/modals/modal.actions";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { toDisplayError } from "utils/errorHandle.function";
import { ReassignReviewActionData, ReassignReviewProps } from "types/modals";

interface RecipientOption {
    value: string;
    label: string;
}

/**
 * Hands a review the user sent over to another reviewer. Same pattern as sending it for review:
 * a reviewer and a mandatory comment.
 */
const ReassignReview = ({ more, closeModal, loopFocus }: ReassignReviewProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const task = more.task;

    const [recipient, setRecipient] = useState<RecipientOption | null>(null);
    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);
    const [refresh, setRefresh] = useState(false);

    const handleSuccess = useCallback(() => {
        more.onReassigned?.();
        closeModal();
    }, [more, closeModal]);

    // the backend refuses the user themselves, the current reviewer and excluded users - the
    // dialog stays open so another reviewer can be picked
    const handleError = useCallback((error: Record<string, any>) => {
        setDisplayError(toDisplayError(error));
        setActionRunning(false);
    }, []);

    const handleReassign = useCallback(() => {
        if (!comment || !recipient?.value || !task?.uid) return;

        setActionRunning(true);
        const data: ReassignReviewActionData = {
            uid: task.uid,
            comment,
            recipient: recipient.value,
        };

        dispatch(reassignReview(data, handleSuccess, handleError));
    }, [comment, recipient, task, dispatch, handleSuccess, handleError]);

    const handleRecipientChange = useCallback((value: RecipientOption | null) => {
        setRecipient(value);
        setRefresh(false);
        setActionRunning(false);
        setDisplayError(null);
    }, []);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setRefresh(false);
        setDisplayError(null);
    }, []);

    const okEnabled = !!(comment && recipient && !actionRunning);

    return (
        <>
            <div className="ga-modal__content">
                {task?.currentAssignee &&
                    <p className="mb-3" data-cy="reassignReview.currentReviewer">
                        {translate("popUp.reassignReview.currentReviewer", task.currentAssignee)}
                    </p>
                }
                <FormField
                    id="reviewer"
                    label={translate("popUp.reassignReview.reviewer")}
                    labelState={`(${translate("popUp.required")})`}
                >
                    <UserSearch
                        propagateValue={handleRecipientChange}
                        tabindex={MODAL_CONTENT_TAB_INDEX}
                        refresh={refresh}
                        // the review can sit in any of the user's companies, not just the current one;
                        // there is no task key of the user's to narrow the search by
                        companyId={task?.companyId}
                        ariaLabel={translate("popUp.reassignReview.reviewer")}
                        placeholder={translate("userSearch.placeholder")}
                        value={recipient}
                        translate={translate}
                        releaseFocus={() => setRefresh(false)}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                    />
                </FormField>
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    inputHint={translate("popUp.reassignReview.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                    id="comment"
                    propagateValue={handleCommentChange}
                    value={comment}
                    label={translate("popUp.reassignReview.commentLabel")}
                    labelState={`(${translate("popUp.required")})`}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                okEnabled={okEnabled}
                okCallback={handleReassign}
                okText={translate("popUp.reassignReview.reassign")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default ReassignReview;
